import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import consoleApi from "../../services/consoleApiService";
import { toast } from "sonner";
import StudioAccounts from "./StudioAccounts";

/**
 * StudioView — Content Studio. A direct compose-and-post tool for the connected
 * Post Bridge accounts: pick account(s), add image(s) (uploaded OR imported from
 * a Content Hub brief's renders), write a caption, then post now or schedule.
 * One post per account (Post Bridge allows one account per platform per post),
 * with an optional per-account caption.
 *
 * Organized as a numbered flow (Accounts → Media → Caption → Publish) with a
 * sticky live preview. Styled to the console's light design tokens; the phone
 * preview stays dark to read like a real Instagram post. Real posting is gated
 * behind a styled confirm step (with a duplicate-publish guard).
 */

const IG_CAPTION_LIMIT = 2200;
const MAX_IMAGES = 10;
const ACCEPTED = "image/jpeg,image/png,image/webp";

const accentFor = (username) => {
  let h = 0;
  for (let i = 0; i < (username || "").length; i += 1) h = (h * 31 + username.charCodeAt(i)) % 360;
  return `linear-gradient(135deg, hsl(${h} 70% 55%), hsl(${(h + 40) % 360} 75% 48%))`;
};
const initials = (username) => (username || "?").replace(/^@/, "").slice(0, 2).toUpperCase();

const asArray = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.briefs) ? data.briefs : Array.isArray(data?.data) ? data.data : [];

// "@a, @b" from a brief's publication summary (skips blanks).
const postedHandles = (pubs) =>
  (pubs || []).map((p) => (p.account_username ? `@${p.account_username}` : null)).filter(Boolean).join(", ");

function AccountChip({ account, selected, posted, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rc-st-acct ${selected ? "is-selected" : ""} ${posted ? "is-posted" : ""}`}
      aria-pressed={selected}
      title={posted ? "This brief was already posted to this account" : undefined}
    >
      <span className="rc-st-avatar" style={{ background: accentFor(account.username) }}>{initials(account.username)}</span>
      <span className="rc-st-acct-meta">
        <span className="rc-st-handle">@{account.username}</span>
        <span className="rc-st-platform">{posted ? "✓ already got this brief" : account.platform}</span>
      </span>
      <span className={`rc-st-check ${selected ? "on" : ""}`} aria-hidden>{selected ? "✓" : ""}</span>
    </button>
  );
}

// Small numbered step header used at the top of each compose card.
function StepHead({ n, title, badge, children }) {
  return (
    <div className="rc-st-card-head">
      <div className="rc-st-card-title">
        <span className="rc-st-step">{n}</span>
        <h2>{title}{badge != null && <span className="rc-st-step-count">{badge}</span>}</h2>
      </div>
      {children}
    </div>
  );
}

export default function StudioView() {
  const [tab, setTab] = useState("compose"); // 'compose' | 'accounts'
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [accountsError, setAccountsError] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [images, setImages] = useState([]); // [{ id, kind:'file'|'url', file?, url, name }]
  const [caption, setCaption] = useState("");
  const [customize, setCustomize] = useState(false);
  const [perAccount, setPerAccount] = useState({});
  const [scheduleMode, setScheduleMode] = useState("now");
  const [scheduledAt, setScheduledAt] = useState("");

  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState(null); // null = closed; object = styled confirm open
  const fileInputRef = useRef(null);

  // Brief import
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [loadingBriefs, setLoadingBriefs] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [importingMode, setImportingMode] = useState(null); // 'carousel' | 'reel' — which import the active button kicked off
  const [importedBriefId, setImportedBriefId] = useState(null);
  const [importedBriefPubs, setImportedBriefPubs] = useState([]); // where this brief already went

  const loadAccounts = useCallback(() => {
    setLoadingAccounts(true);
    setAccountsError(null);
    consoleApi.content
      .studioAccounts()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setAccounts(list);
        if (list.length === 1) setSelectedIds([list[0].id]);
      })
      .catch((e) => setAccountsError(e?.response?.data?.detail || e.message || "Failed to load accounts"))
      .finally(() => setLoadingAccounts(false));
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // Revoke object URLs (file-kind only) on unmount.
  useEffect(() => () => images.forEach((im) => im.kind === "file" && URL.revokeObjectURL(im.url)), [images]);

  const selectedAccounts = useMemo(() => accounts.filter((a) => selectedIds.includes(a.id)), [accounts, selectedIds]);
  const toggleAccount = (id) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // The brief→post link only holds while the brief's renders are still loaded.
  const briefActive = !!importedBriefId && images.some((im) => im.kind === "url");
  const hasVideo = images.some((im) => im.isVideo); // reel loaded — single video post, not a carousel
  const postedAcctIds = useMemo(
    () => new Set((importedBriefPubs || []).map((p) => String(p.postbridge_account_id))),
    [importedBriefPubs]
  );
  // Selected accounts that already received THIS brief — the double-publish risk.
  const dupAccounts = useMemo(
    () => (briefActive ? selectedAccounts.filter((a) => postedAcctIds.has(String(a.id))) : []),
    [briefActive, selectedAccounts, postedAcctIds]
  );

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!incoming.length) return;
    setImages((prev) => {
      const room = Math.max(0, MAX_IMAGES - prev.length);
      const next = incoming.slice(0, room).map((file) => ({
        id: `f-${file.name}-${file.size}-${file.lastModified}-${Math.round(performance.now())}`,
        kind: "file",
        file,
        url: URL.createObjectURL(file),
        name: file.name,
      }));
      if (incoming.length > room) toast.error(`Max ${MAX_IMAGES} images per post.`);
      return [...prev, ...next];
    });
  }, []);

  const removeImage = (id) =>
    setImages((prev) => {
      const t = prev.find((im) => im.id === id);
      if (t && t.kind === "file") URL.revokeObjectURL(t.url);
      return prev.filter((im) => im.id !== id);
    });

  const moveImage = (id, dir) =>
    setImages((prev) => {
      const i = prev.findIndex((im) => im.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  // --- brief import ----------------------------------------------------
  const openBriefPicker = () => {
    setBriefOpen((v) => !v);
    if (!briefs.length) {
      setLoadingBriefs(true);
      consoleApi.content
        .listBriefs({ limit: 50 })
        .then((d) => setBriefs(asArray(d)))
        .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load briefs"))
        .finally(() => setLoadingBriefs(false));
    }
  };

  const importBrief = async (briefId, mode = "carousel") => {
    setImportingId(briefId);
    setImportingMode(mode);
    try {
      const brief = await consoleApi.content.getBrief(briefId);
      let items;
      if (mode === "reel") {
        const reel = brief?.reel;
        if (!reel?.url) {
          toast.error("That brief has no reel yet — build one in Content Hub first.");
          return;
        }
        // A reel is a single 9:16 video post (Instagram Reel / TikTok), never mixed
        // with carousel images. mime_type tells the backend to upload it as video.
        items = [{ id: `reel-${briefId}`, kind: "url", url: reel.url, name: `${briefId}-reel.mp4`, isVideo: true, mime_type: "video/mp4" }];
      } else {
        const renders = [...(brief?.renders || [])].sort((a, b) => (a.slide_index || 0) - (b.slide_index || 0));
        if (!renders.length) {
          toast.error("That brief has no renders yet — render it in Content Hub first.");
          return;
        }
        items = renders.slice(0, MAX_IMAGES).map((r, i) => ({
          id: `u-${briefId}-${r.slide_index ?? i}`,
          kind: "url",
          url: r.url,
          name: `${briefId}-slide${r.slide_index ?? i}.png`,
        }));
      }
      // Clear any existing file object URLs, then load the imported media.
      setImages((prev) => {
        prev.forEach((im) => im.kind === "file" && URL.revokeObjectURL(im.url));
        return items;
      });
      const igCaption = brief?.captions?.instagram || brief?.captions?.tiktok || Object.values(brief?.captions || {}).find(Boolean) || "";
      if (igCaption) setCaption(igCaption);
      // Remember which brief this media came from + where it's already been
      // posted, so we can flag accounts and warn before double-publishing.
      setImportedBriefId(briefId);
      setImportedBriefPubs(Array.isArray(brief?.publications) ? brief.publications : []);
      setBriefOpen(false);
      const pubs = Array.isArray(brief?.publications) ? brief.publications : [];
      toast.success(mode === "reel" ? "Imported reel from brief." : `Imported ${items.length} render${items.length > 1 ? "s" : ""} from brief.`);
      if (pubs.length) toast.warning(`Heads up — this brief was already posted to ${postedHandles(pubs)}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Import failed");
    } finally {
      setImportingId(null);
      setImportingMode(null);
    }
  };

  // --- post ------------------------------------------------------------
  const showDuplicateWarning = selectedIds.length > 1 && !customize;
  const canPost =
    selectedIds.length > 0 && images.length > 0 && !posting &&
    (scheduleMode === "now" || (scheduleMode === "schedule" && scheduledAt));

  // Validate + open the styled confirm modal (replaces the native confirm()).
  const requestPost = () => {
    if (!canPost) return;
    const isSchedule = scheduleMode === "schedule";
    let isoSchedule = null;
    if (isSchedule) {
      const d = new Date(scheduledAt);
      if (Number.isNaN(d.getTime())) return toast.error("Pick a valid date & time.");
      if (d.getTime() < Date.now()) return toast.error("Scheduled time must be in the future.");
      isoSchedule = d.toISOString();
    }
    setConfirmInfo({
      isSchedule,
      isoSchedule,
      verb: isSchedule ? "Schedule" : "Post",
      handles: selectedAccounts.map((a) => `@${a.username}`).join(", "),
      count: selectedIds.length,
      dup: dupAccounts.map((a) => `@${a.username}`),
    });
  };

  const executePost = async () => {
    const info = confirmInfo;
    if (!info) return;
    setConfirmInfo(null);

    // Link the post back to its source brief (only while its renders are loaded)
    // so the brief records where it went and warns next time.
    const briefId = images.some((im) => im.kind === "url") ? importedBriefId : null;

    const fd = new FormData();
    images.filter((im) => im.kind === "file").forEach((im) => fd.append("files", im.file, im.name));
    const urlMedia = images
      .filter((im) => im.kind === "url")
      .map((im) => ({ url: im.url, name: im.name, ...(im.mime_type ? { mime_type: im.mime_type } : {}) }));
    if (urlMedia.length) fd.append("media_urls", JSON.stringify(urlMedia));
    fd.append("account_ids", JSON.stringify(selectedIds));
    fd.append("caption", caption || "");
    if (briefId) fd.append("brief_id", briefId);
    if (customize) {
      const map = {};
      selectedIds.forEach((id) => { if (perAccount[id]) map[id] = perAccount[id]; });
      if (Object.keys(map).length) fd.append("per_account_captions", JSON.stringify(map));
    }
    if (info.isoSchedule) fd.append("scheduled_at", info.isoSchedule);

    setPosting(true);
    setResults(null);
    try {
      const res = await consoleApi.content.studioPost(fd);
      const out = res?.results || [];
      setResults(out);
      // Reflect what just went out so a second attempt this session warns too.
      if (briefId) {
        const justPosted = out
          .filter((r) => r.status === "posted" || r.status === "scheduled")
          .map((r) => ({ postbridge_account_id: String(r.account_id), account_username: r.username, status: r.status, posted_at: new Date().toISOString() }));
        if (justPosted.length) {
          setImportedBriefPubs((prev) => {
            const seen = new Set(prev.map((p) => String(p.postbridge_account_id)));
            return [...prev, ...justPosted.filter((p) => !seen.has(p.postbridge_account_id))];
          });
        }
      }
      const ok = res?.posted ?? 0;
      const fail = res?.failed ?? 0;
      if (fail === 0) toast.success(`${info.isSchedule ? "Scheduled" : "Posted"} to ${ok} account${ok > 1 ? "s" : ""}.`);
      else if (ok === 0) toast.error(`All ${fail} failed. See details below.`);
      else toast(`${ok} ${info.isSchedule ? "scheduled" : "posted"}, ${fail} failed.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Post failed.");
    } finally {
      setPosting(false);
    }
  };

  const previewAccount = selectedAccounts[0] || accounts[0] || { username: "your_account" };
  const previewCaption = (customize && perAccount[previewAccount.id]) || caption || "";
  const acctCountLabel = accounts.length ? `${accounts.length} account${accounts.length === 1 ? "" : "s"} connected` : null;

  return (
    <div className="rc-studio">
      <style>{STUDIO_CSS}</style>

      {/* Header */}
      <div className="rc-st-head">
        <div className="rc-st-head-id">
          <span className="rc-st-brandmark" aria-hidden />
          <div>
            <p className="rc-st-eyebrow">Content Studio</p>
            <h1 className="rc-st-title">Instagram Studio</h1>
            <p className="rc-st-sub">Compose &amp; publish to your connected accounts — or import a Content Hub brief.</p>
          </div>
        </div>
        <div className="rc-st-head-actions">
          {acctCountLabel && <span className="rc-st-conn"><span className="rc-st-conn-dot" />{acctCountLabel}</span>}
          <button type="button" className="rc-st-ghost" onClick={loadAccounts} disabled={loadingAccounts}>
            {loadingAccounts ? "Refreshing…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* Tabs: compose vs per-account track record */}
      <div className="rc-st-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "compose"} className={tab === "compose" ? "on" : ""} onClick={() => setTab("compose")}>Compose</button>
        <button type="button" role="tab" aria-selected={tab === "accounts"} className={tab === "accounts" ? "on" : ""} onClick={() => setTab("accounts")}>Accounts</button>
      </div>

      {tab === "accounts" && <StudioAccounts active={tab === "accounts"} />}

      <div className="rc-st-grid" style={tab === "compose" ? undefined : { display: "none" }}>
        <div className="rc-st-compose">
          {/* Step 1 — Accounts */}
          <section className="rc-st-card">
            <StepHead n={1} title="Accounts" badge={selectedIds.length ? `${selectedIds.length} selected` : null}>
              {accounts.length > 1 && (
                <div className="rc-st-mini-actions">
                  <button type="button" onClick={() => setSelectedIds(accounts.map((a) => a.id))}>Select all</button>
                  <span>·</span>
                  <button type="button" onClick={() => setSelectedIds([])}>Clear</button>
                </div>
              )}
            </StepHead>
            {loadingAccounts ? <p className="rc-st-muted">Loading accounts…</p>
              : accountsError ? <p className="rc-st-error">{accountsError}</p>
              : accounts.length === 0 ? <p className="rc-st-muted">No connected accounts found in Post Bridge.</p>
              : (
                <div className="rc-st-accts">
                  {accounts.map((a) => (
                    <AccountChip
                      key={a.id}
                      account={a}
                      selected={selectedIds.includes(a.id)}
                      posted={briefActive && postedAcctIds.has(String(a.id))}
                      onToggle={() => toggleAccount(a.id)}
                    />
                  ))}
                </div>
              )}
            {briefActive && importedBriefPubs.length > 0 && (
              <div className="rc-st-note rc-st-note--amber">
                <strong>Already published.</strong> This brief has gone to {postedHandles(importedBriefPubs)}. Posting to those again will double-publish.
              </div>
            )}
          </section>

          {/* Step 2 — Media */}
          <section className="rc-st-card">
            <StepHead n={2} title="Media" badge={hasVideo ? "Reel" : images.length > 1 ? `Carousel · ${images.length}` : null}>
              <div className="rc-st-mini-actions">
                <button type="button" onClick={openBriefPicker}>{briefOpen ? "Close" : "⤓ Import from brief"}</button>
                {images.length > 0 && !hasVideo && <><span>·</span><button type="button" onClick={() => fileInputRef.current?.click()}>+ Add</button></>}
              </div>
            </StepHead>

            {briefOpen && (
              <div className="rc-st-briefs">
                {loadingBriefs ? <p className="rc-st-muted">Loading briefs…</p>
                  : briefs.length === 0 ? <p className="rc-st-muted">No briefs found.</p>
                  : (
                    <div className="rc-st-brief-list">
                      {briefs.map((b) => {
                        const rc = Array.isArray(b.renders) ? b.renders.length : (b.render_count || 0);
                        const pubs = Array.isArray(b.publications) ? b.publications : [];
                        const loadingThis = importingId === b.id;
                        return (
                          <div key={b.id} className="rc-st-brief">
                            <div className="rc-st-brief-id">
                              <span className="rc-st-brief-topic">{b.topic || b.title || "Untitled brief"}</span>
                              <span className="rc-st-brief-meta">
                                {b.city ? `${b.city} · ` : ""}{b.platform || "—"}{rc ? ` · ${rc} render${rc > 1 ? "s" : ""}` : " · no renders"}
                              </span>
                            </div>
                            {pubs.length > 0 && (
                              <span className="rc-st-brief-posted" title={`Posted to ${postedHandles(pubs)}`}>
                                ● Posted{pubs.length > 1 ? ` ×${pubs.length}` : ""}
                              </span>
                            )}
                            <div className="rc-st-brief-actions">
                              <button type="button" className="rc-st-brief-act" disabled={!!importingId} onClick={() => importBrief(b.id, "carousel")}>
                                {loadingThis && importingMode === "carousel" ? "…" : "Carousel"}
                              </button>
                              <button
                                type="button"
                                className="rc-st-brief-act rc-st-brief-act--reel"
                                disabled={!!importingId}
                                onClick={() => importBrief(b.id, "reel")}
                                title="Post the brief's motion reel (build it in Content Hub first)"
                              >
                                {loadingThis && importingMode === "reel" ? "…" : "Reel"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            )}

            {images.length === 0 ? (
              <div
                className={`rc-st-drop ${dragOver ? "is-over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button" tabIndex={0}
              >
                <div className="rc-st-drop-icon">⬆</div>
                <p className="rc-st-drop-title">Drag images here</p>
                <p className="rc-st-muted">or click to browse · JPG / PNG / WebP · up to {MAX_IMAGES}</p>
              </div>
            ) : (
              <div className="rc-st-thumbs">
                {images.map((im, i) => (
                  <div key={im.id} className="rc-st-thumb">
                    {im.isVideo
                      ? <video src={im.url} muted playsInline preload="metadata" />
                      : <img src={im.url} alt={`upload ${i + 1}`} />}
                    <span className="rc-st-thumb-idx">{i + 1}</span>
                    {im.kind === "url" && <span className="rc-st-thumb-tag">{im.isVideo ? "reel" : "brief"}</span>}
                    <div className="rc-st-thumb-ctl">
                      {!im.isVideo && <button type="button" title="Move left" disabled={i === 0} onClick={() => moveImage(im.id, -1)}>‹</button>}
                      <button type="button" title="Remove" className="rc-st-thumb-x" onClick={() => removeImage(im.id)}>×</button>
                      {!im.isVideo && <button type="button" title="Move right" disabled={i === images.length - 1} onClick={() => moveImage(im.id, 1)}>›</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <input ref={fileInputRef} type="file" accept={ACCEPTED} multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          </section>

          {/* Step 3 — Caption */}
          <section className="rc-st-card">
            <StepHead n={3} title="Caption">
              <span className={`rc-st-count ${caption.length > IG_CAPTION_LIMIT ? "over" : ""}`}>{caption.length}/{IG_CAPTION_LIMIT}</span>
            </StepHead>
            <textarea className="rc-st-textarea" rows={5} placeholder="Write a caption… add hashtags, mentions, line breaks." value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={IG_CAPTION_LIMIT + 200} />
            {selectedIds.length > 1 && (
              <label className="rc-st-toggle-row">
                <input type="checkbox" checked={customize} onChange={(e) => setCustomize(e.target.checked)} />
                <span>Customize caption per account <em>(recommended for multiple accounts)</em></span>
              </label>
            )}
            {customize && selectedIds.length > 1 && (
              <div className="rc-st-per">
                {selectedAccounts.map((a) => (
                  <div key={a.id} className="rc-st-per-row">
                    <div className="rc-st-per-handle">@{a.username}</div>
                    <textarea rows={2} placeholder={`Caption for @${a.username} (defaults to the shared caption)`} value={perAccount[a.id] ?? ""} onChange={(e) => setPerAccount((p) => ({ ...p, [a.id]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}
            {showDuplicateWarning && (
              <div className="rc-st-note rc-st-note--amber">⚠ Posting the same image &amp; caption to multiple accounts can trip Instagram's duplicate detection and hurt reach. Turn on “Customize per account” to vary the caption.</div>
            )}
          </section>

          {/* Step 4 — Publish */}
          <section className="rc-st-card rc-st-publish">
            <StepHead n={4} title="Publish" />
            <div className="rc-st-seg">
              <button type="button" className={scheduleMode === "now" ? "on" : ""} onClick={() => setScheduleMode("now")}>Post now</button>
              <button type="button" className={scheduleMode === "schedule" ? "on" : ""} onClick={() => setScheduleMode("schedule")}>Schedule</button>
            </div>
            {scheduleMode === "schedule" && (
              <input className="rc-st-datetime" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            )}
            <button type="button" className="rc-st-post" disabled={!canPost} onClick={requestPost}>
              {posting ? "Publishing…"
                : scheduleMode === "schedule" ? `Schedule for ${selectedIds.length || 0} account${selectedIds.length === 1 ? "" : "s"}`
                : `Post to ${selectedIds.length || 0} account${selectedIds.length === 1 ? "" : "s"}`}
            </button>
            {!canPost && !posting && (
              <p className="rc-st-hint">
                {selectedIds.length === 0 ? "Select at least one account."
                  : images.length === 0 ? "Add at least one image."
                  : scheduleMode === "schedule" && !scheduledAt ? "Pick a date & time." : ""}
              </p>
            )}
            {results && (
              <div className="rc-st-results">
                {results.map((r) => (
                  <div key={r.account_id} className={`rc-st-result ${r.status}`}>
                    <span className="rc-st-result-dot" />
                    <span className="rc-st-result-handle">@{r.username}</span>
                    <span className="rc-st-result-status">{r.status === "failed" ? (r.error || "failed") : r.status}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Live preview */}
        <div className="rc-st-preview-wrap">
          <p className="rc-st-preview-label">Live preview</p>
          <div className="rc-st-phone">
            <div className="rc-st-ig-top">
              <span className="rc-st-ig-avatar" style={{ background: accentFor(previewAccount.username) }}>{initials(previewAccount.username)}</span>
              <span className="rc-st-ig-handle">@{previewAccount.username}</span>
              <span className="rc-st-ig-dots">⋯</span>
            </div>
            <div className="rc-st-ig-media">
              {images.length > 0
                ? (images[0].isVideo
                    ? <video src={images[0].url} muted playsInline loop autoPlay />
                    : <img src={images[0].url} alt="preview" />)
                : <div className="rc-st-ig-empty">Your image preview</div>}
              {images.length > 1 && (
                <div className="rc-st-ig-dots-row">{images.slice(0, 8).map((im, i) => <span key={im.id} className={i === 0 ? "on" : ""} />)}</div>
              )}
            </div>
            <div className="rc-st-ig-actions"><span>♡</span><span>💬</span><span>➤</span></div>
            <div className="rc-st-ig-caption">
              <strong>@{previewAccount.username}</strong>{" "}
              {previewCaption ? previewCaption : <span className="rc-st-ig-dim">your caption appears here…</span>}
            </div>
          </div>
          {selectedIds.length > 1 && (
            <p className="rc-st-preview-note">Showing @{previewAccount.username}. {selectedIds.length} accounts selected — each gets its own post.</p>
          )}
        </div>
      </div>

      {/* Styled confirm (replaces native window.confirm) */}
      {confirmInfo && (
        <div className="rc-st-modal-scrim" onClick={() => setConfirmInfo(null)}>
          <div className="rc-st-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3 className="rc-st-modal-title">{confirmInfo.verb} to {confirmInfo.count} account{confirmInfo.count === 1 ? "" : "s"}?</h3>
            <p className="rc-st-modal-handles">{confirmInfo.handles}</p>
            {confirmInfo.dup.length > 0 && (
              <div className="rc-st-note rc-st-note--amber" style={{ marginTop: 0 }}>
                <strong>⚠ Duplicate.</strong> You already posted this brief to {confirmInfo.dup.join(", ")}. Posting again will double-publish.
              </div>
            )}
            <p className="rc-st-modal-real">This publishes to real Instagram accounts.</p>
            <div className="rc-st-modal-actions">
              <button type="button" className="rc-st-ghost" onClick={() => setConfirmInfo(null)}>Cancel</button>
              <button type="button" className="rc-st-post rc-st-modal-go" onClick={executePost}>{confirmInfo.verb} now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STUDIO_CSS = `
  .rc-studio { --ig:linear-gradient(135deg,#f9ce34 0%,#ee2a7b 45%,#6228d7 100%);
    color:var(--console-ink); max-width:1180px; margin:0 auto; padding:4px 2px 60px; }

  /* Header */
  .rc-st-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
  .rc-st-head-id { display:flex; gap:14px; align-items:flex-start; }
  .rc-st-brandmark { width:46px; height:46px; border-radius:13px; background:var(--ig); flex-shrink:0; box-shadow:0 6px 16px rgba(214,41,118,.30); position:relative; }
  .rc-st-brandmark::after { content:""; position:absolute; inset:11px; border:2.5px solid #fff; border-radius:8px; }
  .rc-st-brandmark::before { content:""; position:absolute; top:13px; right:13px; width:5px; height:5px; border-radius:50%; background:#fff; }
  .rc-st-eyebrow { font-size:11px; letter-spacing:.26em; text-transform:uppercase; color:var(--console-accent); margin:0 0 4px; font-weight:700; }
  .rc-st-title { font-size:25px; font-weight:800; letter-spacing:-.02em; margin:0; color:var(--console-ink); line-height:1.1; }
  .rc-st-sub { color:var(--console-muted); margin:6px 0 0; font-size:13.5px; max-width:520px; line-height:1.5; }
  .rc-st-head-actions { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
  .rc-st-conn { display:inline-flex; align-items:center; gap:7px; font-size:12.5px; font-weight:600; color:var(--console-muted); }
  .rc-st-conn-dot { width:8px; height:8px; border-radius:50%; background:#22a565; box-shadow:0 0 0 3px rgba(34,165,101,.16); }
  .rc-st-ghost { background:var(--console-surface); color:var(--console-ink); border:1px solid var(--console-border); border-radius:10px; padding:9px 14px; font-size:13px; font-weight:600; cursor:pointer; box-shadow:var(--console-shadow); transition:background .14s, border-color .14s; }
  .rc-st-ghost:hover { background:var(--console-s2); }
  .rc-st-ghost:disabled { opacity:.6; cursor:default; }

  /* Tabs */
  .rc-st-tabs { display:inline-flex; gap:4px; background:var(--console-bg); border:1px solid var(--console-border); border-radius:11px; padding:4px; margin-bottom:20px; }
  .rc-st-tabs button { background:none; border:none; color:var(--console-muted); font-weight:700; font-size:13px; padding:8px 20px; border-radius:8px; cursor:pointer; transition:color .14s; }
  .rc-st-tabs button:hover { color:var(--console-ink); }
  .rc-st-tabs button.on { background:var(--console-surface); color:var(--console-ink); box-shadow:var(--console-shadow); }

  /* Layout */
  .rc-st-grid { display:grid; grid-template-columns:minmax(0,1fr) 360px; gap:24px; align-items:start; }
  @media (max-width:980px){ .rc-st-grid{ grid-template-columns:1fr; } .rc-st-preview-wrap{ order:-1; } }
  .rc-st-compose { display:flex; flex-direction:column; gap:16px; min-width:0; }
  .rc-st-card { background:var(--console-surface); border:1px solid var(--console-border); border-radius:16px; padding:18px 20px; box-shadow:var(--console-shadow); }
  .rc-st-card-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:15px; }
  .rc-st-card-title { display:flex; align-items:center; gap:11px; min-width:0; }
  .rc-st-step { flex-shrink:0; width:24px; height:24px; border-radius:50%; background:var(--console-accent-bg); color:var(--console-accent); font-size:12.5px; font-weight:800; display:flex; align-items:center; justify-content:center; }
  .rc-st-card-head h2 { font-size:14.5px; font-weight:700; margin:0; display:flex; align-items:center; gap:9px; color:var(--console-ink); }
  .rc-st-step-count { font-size:11px; font-weight:700; color:var(--console-accent); background:var(--console-accent-bg); padding:3px 9px; border-radius:999px; letter-spacing:.01em; }
  .rc-st-mini-actions { font-size:12.5px; color:var(--console-muted); display:flex; gap:8px; align-items:center; }
  .rc-st-mini-actions button { background:none; border:none; color:var(--console-accent); cursor:pointer; font-size:12.5px; font-weight:600; padding:0; }
  .rc-st-mini-actions button:hover { text-decoration:underline; }
  .rc-st-muted { color:var(--console-muted); font-size:13px; margin:0; }
  .rc-st-error { color:var(--console-red); font-size:13px; margin:0; }

  /* Account chips */
  .rc-st-accts { display:grid; grid-template-columns:repeat(auto-fill,minmax(185px,1fr)); gap:10px; }
  .rc-st-acct { display:flex; align-items:center; gap:10px; text-align:left; background:var(--console-bg); border:1px solid var(--console-border); border-radius:12px; padding:10px; cursor:pointer; transition:border-color .14s, background .14s, box-shadow .14s, transform .1s; }
  .rc-st-acct:hover { border-color:var(--console-accent); transform:translateY(-1px); }
  .rc-st-acct.is-selected { border-color:var(--console-accent); box-shadow:0 0 0 1px var(--console-accent) inset; background:var(--console-accent-bg); }
  .rc-st-acct.is-posted:not(.is-selected) { border-color:var(--console-amber-bd); background:var(--console-amber-bg); }
  .rc-st-acct.is-posted .rc-st-platform { color:#9a6a00; font-weight:700; }
  .rc-st-avatar { width:38px; height:38px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:13px; color:#fff; }
  .rc-st-acct-meta { display:flex; flex-direction:column; min-width:0; }
  .rc-st-handle { font-weight:700; font-size:13px; color:var(--console-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .rc-st-platform { font-size:11px; color:var(--console-muted); text-transform:capitalize; }
  .rc-st-check { margin-left:auto; width:20px; height:20px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:800; color:#fff; background:transparent; border:1px solid var(--console-border); transition:background .14s, border-color .14s; }
  .rc-st-check.on { background:var(--console-accent); border-color:var(--console-accent); }

  /* Brief picker */
  .rc-st-briefs { margin-bottom:14px; border:1px solid var(--console-border); border-radius:12px; background:var(--console-bg); padding:8px; max-height:230px; overflow:auto; }
  .rc-st-brief-list { display:flex; flex-direction:column; gap:6px; }
  .rc-st-brief { display:flex; align-items:center; gap:10px; text-align:left; background:var(--console-surface); border:1px solid var(--console-border); border-radius:10px; padding:9px 11px; transition:border-color .14s; }
  .rc-st-brief:hover { border-color:var(--console-accent); }
  .rc-st-brief-id { display:flex; flex-direction:column; gap:2px; min-width:0; }
  .rc-st-brief-topic { font-weight:700; font-size:13px; color:var(--console-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%; }
  .rc-st-brief-meta { font-size:12px; color:var(--console-muted); }
  .rc-st-brief-actions { margin-left:auto; display:flex; gap:6px; flex:0 0 auto; }
  .rc-st-brief-act { font:inherit; font-size:12px; font-weight:700; padding:6px 11px; border-radius:8px; border:1px solid var(--console-border); background:var(--console-bg); color:var(--console-ink); cursor:pointer; transition:border-color .14s, color .14s; }
  .rc-st-brief-act:hover:not(:disabled) { border-color:var(--console-accent); color:var(--console-accent); }
  .rc-st-brief-act--reel { color:var(--console-accent); }
  .rc-st-brief-act:disabled { opacity:.5; cursor:default; }
  .rc-st-brief-posted { font-size:10px; font-weight:800; letter-spacing:.03em; text-transform:uppercase; color:#9a6a00; background:var(--console-amber-bg); border:1px solid var(--console-amber-bd); padding:2px 7px; border-radius:6px; white-space:nowrap; }

  /* Media drop + thumbs */
  .rc-st-drop { border:1.5px dashed var(--console-border); border-radius:14px; padding:38px 16px; text-align:center; cursor:pointer; transition:border-color .14s, background .14s; background:var(--console-bg); }
  .rc-st-drop:hover, .rc-st-drop.is-over { border-color:var(--console-accent); background:var(--console-accent-bg); }
  .rc-st-drop-icon { font-size:26px; opacity:.7; }
  .rc-st-drop-title { font-weight:700; margin:10px 0 2px; color:var(--console-ink); }
  .rc-st-thumbs { display:grid; grid-template-columns:repeat(auto-fill,minmax(96px,1fr)); gap:10px; }
  .rc-st-thumb { position:relative; aspect-ratio:1; border-radius:12px; overflow:hidden; border:1px solid var(--console-border); background:var(--console-bg); }
  .rc-st-thumb img, .rc-st-thumb video { width:100%; height:100%; object-fit:cover; display:block; }
  .rc-st-thumb-idx { position:absolute; top:6px; left:6px; background:rgba(0,0,0,.62); color:#fff; font-size:11px; font-weight:700; width:20px; height:20px; border-radius:50%; display:flex; align-items:center; justify-content:center; }
  .rc-st-thumb-tag { position:absolute; top:6px; right:6px; background:var(--console-accent); color:#fff; font-size:9px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; padding:2px 5px; border-radius:5px; }
  .rc-st-thumb-ctl { position:absolute; inset:auto 0 0 0; display:flex; justify-content:space-between; padding:4px; background:linear-gradient(transparent, rgba(0,0,0,.62)); opacity:0; transition:opacity .14s; }
  .rc-st-thumb:hover .rc-st-thumb-ctl { opacity:1; }
  .rc-st-thumb-ctl button { background:rgba(0,0,0,.6); color:#fff; border:none; width:24px; height:24px; border-radius:6px; cursor:pointer; font-size:15px; line-height:1; display:flex; align-items:center; justify-content:center; }
  .rc-st-thumb-ctl button:disabled { opacity:.3; cursor:default; }
  .rc-st-thumb-x { background:rgba(220,40,40,.88) !important; }

  /* Caption */
  .rc-st-textarea, .rc-st-per-row textarea { width:100%; background:var(--console-bg); border:1px solid var(--console-border); border-radius:11px; color:var(--console-ink); padding:12px; font-size:14px; font-family:inherit; resize:vertical; line-height:1.5; transition:border-color .14s, box-shadow .14s; }
  .rc-st-textarea:focus, .rc-st-per-row textarea:focus { outline:none; border-color:var(--console-accent); box-shadow:0 0 0 3px var(--console-accent-bg); }
  .rc-st-count { font-size:12px; color:var(--console-muted); font-variant-numeric:tabular-nums; }
  .rc-st-count.over { color:var(--console-red); font-weight:700; }
  .rc-st-toggle-row { display:flex; align-items:center; gap:9px; margin-top:12px; font-size:13px; color:var(--console-ink); cursor:pointer; }
  .rc-st-toggle-row em { color:var(--console-muted); font-style:normal; }
  .rc-st-per { margin-top:12px; display:flex; flex-direction:column; gap:10px; }
  .rc-st-per-handle { font-size:12px; font-weight:700; color:var(--console-muted); margin-bottom:5px; }

  /* Notes (amber callouts: dup / posted / duplicate-detection) */
  .rc-st-note { margin-top:12px; border-radius:10px; padding:10px 12px; font-size:12.5px; line-height:1.5; }
  .rc-st-note--amber { background:var(--console-amber-bg); border:1px solid var(--console-amber-bd); color:#9a6a00; }
  .rc-st-note strong { font-weight:800; }

  /* Publish */
  .rc-st-publish { position:relative; }
  .rc-st-seg { display:flex; background:var(--console-bg); border:1px solid var(--console-border); border-radius:11px; padding:4px; gap:4px; margin-bottom:12px; }
  .rc-st-seg button { flex:1; background:none; border:none; color:var(--console-muted); font-weight:700; font-size:13px; padding:10px; border-radius:8px; cursor:pointer; transition:color .14s; }
  .rc-st-seg button:hover { color:var(--console-ink); }
  .rc-st-seg button.on { background:var(--console-accent); color:#fff; }
  .rc-st-datetime { width:100%; background:var(--console-bg); border:1px solid var(--console-border); border-radius:10px; color:var(--console-ink); padding:11px 12px; font-size:14px; margin-bottom:12px; }
  .rc-st-post { width:100%; background:var(--ig); color:#fff; border:none; border-radius:13px; padding:15px; font-size:15px; font-weight:800; cursor:pointer; transition:filter .15s, transform .1s, box-shadow .15s; box-shadow:0 6px 18px rgba(214,41,118,.28); }
  .rc-st-post:hover:not(:disabled){ filter:brightness(1.06); box-shadow:0 8px 22px rgba(214,41,118,.34); }
  .rc-st-post:active:not(:disabled){ transform:translateY(1px); }
  .rc-st-post:disabled { background:var(--console-s2); color:var(--console-dim); cursor:not-allowed; box-shadow:none; }
  .rc-st-hint { color:var(--console-muted); font-size:12px; text-align:center; margin:10px 0 0; }
  .rc-st-results { margin-top:14px; display:flex; flex-direction:column; gap:8px; }
  .rc-st-result { display:flex; align-items:center; gap:10px; background:var(--console-bg); border:1px solid var(--console-border); border-radius:10px; padding:9px 12px; font-size:13px; }
  .rc-st-result-dot { width:9px; height:9px; border-radius:50%; flex-shrink:0; }
  .rc-st-result.posted .rc-st-result-dot, .rc-st-result.scheduled .rc-st-result-dot { background:#22a565; }
  .rc-st-result.failed .rc-st-result-dot { background:#e23e3e; }
  .rc-st-result-handle { font-weight:700; color:var(--console-ink); }
  .rc-st-result-status { margin-left:auto; color:var(--console-muted); text-transform:capitalize; text-align:right; }
  .rc-st-result.failed .rc-st-result-status { color:#c33; text-transform:none; }

  /* Preview */
  .rc-st-preview-wrap { position:sticky; top:16px; }
  .rc-st-preview-label { font-size:11px; letter-spacing:.22em; text-transform:uppercase; color:var(--console-muted); font-weight:700; margin:0 0 10px; }
  .rc-st-phone { background:#000; border:1px solid var(--console-border); border-radius:18px; overflow:hidden; box-shadow:var(--console-shadow-md); }
  .rc-st-ig-top { display:flex; align-items:center; gap:9px; padding:11px 12px; }
  .rc-st-ig-avatar { width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:800; color:#fff; }
  .rc-st-ig-handle { font-size:13px; font-weight:700; color:#fff; }
  .rc-st-ig-dots { margin-left:auto; color:#fff; }
  .rc-st-ig-media { position:relative; aspect-ratio:1; background:#0c0c0c; }
  .rc-st-ig-media img { width:100%; height:100%; object-fit:cover; display:block; }
  .rc-st-ig-empty { width:100%; height:100%; display:flex; align-items:center; justify-content:center; color:#666; font-size:13px; }
  .rc-st-ig-dots-row { position:absolute; bottom:8px; left:0; right:0; display:flex; justify-content:center; gap:5px; }
  .rc-st-ig-dots-row span { width:6px; height:6px; border-radius:50%; background:rgba(255,255,255,.4); }
  .rc-st-ig-dots-row span.on { background:#fff; }
  .rc-st-ig-actions { display:flex; gap:14px; padding:10px 12px 6px; font-size:18px; color:#fff; }
  .rc-st-ig-caption { padding:0 12px 14px; font-size:13px; color:#eee; line-height:1.5; white-space:pre-wrap; word-break:break-word; }
  .rc-st-ig-dim { color:#777; }
  .rc-st-preview-note { font-size:12px; color:var(--console-muted); margin:10px 2px 0; line-height:1.5; }

  /* Confirm modal */
  .rc-st-modal-scrim { position:fixed; inset:0; background:rgba(15,18,28,.55); backdrop-filter:blur(2px); display:flex; align-items:center; justify-content:center; padding:20px; z-index:1000; animation:rc-st-fade .14s ease; }
  .rc-st-modal { background:var(--console-surface); border:1px solid var(--console-border); border-radius:18px; box-shadow:var(--console-shadow-md); padding:24px; width:100%; max-width:440px; animation:rc-st-pop .16s ease; }
  .rc-st-modal-title { font-size:18px; font-weight:800; margin:0 0 6px; color:var(--console-ink); letter-spacing:-.01em; }
  .rc-st-modal-handles { font-size:13.5px; color:var(--console-accent); font-weight:700; margin:0 0 12px; word-break:break-word; }
  .rc-st-modal-real { font-size:12.5px; color:var(--console-muted); margin:12px 0 0; }
  .rc-st-modal-actions { display:flex; gap:10px; margin-top:18px; }
  .rc-st-modal-actions .rc-st-ghost { flex:0 0 auto; padding:13px 20px; }
  .rc-st-modal-go { flex:1; }
  @keyframes rc-st-fade { from{ opacity:0 } to{ opacity:1 } }
  @keyframes rc-st-pop { from{ opacity:0; transform:translateY(8px) scale(.98) } to{ opacity:1; transform:none } }
`;
